/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */
import type React from "react";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useReducer } from "react";
import type { Item, Session, WebSocketMessage } from "../types";

interface RetroState {
	session: Session | null;
	userId: string;
	userName: string;
	ws: WebSocket | null;
	error: string | null;
	loading: boolean;
	sessionClosed: boolean;
}

type RetroAction =
	| { type: "SET_LOADING"; payload: boolean }
	| { type: "SET_ERROR"; payload: string | null }
	| { type: "SET_SESSION"; payload: Session | null }
	| { type: "SET_USER_ID"; payload: string }
	| { type: "SET_USER_NAME"; payload: string }
	| { type: "SET_WS"; payload: WebSocket | null }
	| { type: "ADD_ITEM"; payload: Item }
	| { type: "UPDATE_ITEM"; payload: Item }
	| { type: "MERGE_ITEMS"; payload: { sourceIds: string[]; newItem: Item } }
	| { type: "VOTE_ITEM"; payload: { itemId: string; userId: string } }
	| { type: "SET_PHASE"; payload: Session["phase"] }
	| { type: "CLOSE_SESSION" };

const initialState: RetroState = {
	session: null,
	userId: "",
	userName: "",
	ws: null,
	error: null,
	loading: false,
	sessionClosed: false,
};

function retroReducer(state: RetroState, action: RetroAction): RetroState {
	switch (action.type) {
		case "SET_LOADING":
			return { ...state, loading: action.payload };
		case "SET_ERROR":
			return { ...state, error: action.payload };
		case "SET_SESSION":
			return { ...state, session: action.payload, sessionClosed: false };
		case "SET_USER_ID":
			return { ...state, userId: action.payload };
		case "SET_USER_NAME":
			return { ...state, userName: action.payload };
		case "SET_WS":
			return { ...state, ws: action.payload };
		case "ADD_ITEM":
			if (!state.session) return state;
			return {
				...state,
				session: {
					...state.session,
					items: {
						...state.session.items,
						[action.payload.id]: action.payload,
					},
				},
			};
		case "UPDATE_ITEM":
			if (!state.session) return state;
			return {
				...state,
				session: {
					...state.session,
					items: {
						...state.session.items,
						[action.payload.id]: action.payload,
					},
				},
			};
		case "MERGE_ITEMS": {
			if (!state.session) return state;
			const newItems = { ...state.session.items };
			for (const id of action.payload.sourceIds) {
				delete newItems[id];
			}
			newItems[action.payload.newItem.id] = action.payload.newItem;
			return {
				...state,
				session: {
					...state.session,
					items: newItems,
				},
			};
		}
		case "VOTE_ITEM": {
			if (!state.session) return state;
			const item = state.session.items[action.payload.itemId];
			if (!item) return state;

			const votes = item.votes.includes(action.payload.userId)
				? item.votes.filter((id) => id !== action.payload.userId)
				: [...item.votes, action.payload.userId];

			return {
				...state,
				session: {
					...state.session,
					items: {
						...state.session.items,
						[action.payload.itemId]: {
							...item,
							votes,
						},
					},
				},
			};
		}
		case "SET_PHASE":
			if (!state.session) return state;
			return {
				...state,
				session: {
					...state.session,
					phase: action.payload,
				},
			};
		case "CLOSE_SESSION":
			// Clear session ID from URL when session is closed
			if (typeof window !== "undefined") {
				const url = new URL(window.location.href);
				if (url.searchParams.has("session")) {
					url.searchParams.delete("session");
					window.history.replaceState({}, "", url.toString());
				}
			}
			return {
				...state,
				session: null,
				ws: null,
				sessionClosed: true,
				// Keep userName and userId for next session
			};
		default:
			return state;
	}
}

interface RetroContextType {
	state: RetroState;
	dispatch: React.Dispatch<RetroAction>;
	createSession: (userName: string) => Promise<void>;
	joinSession: (sessionId: string, userName: string) => Promise<void>;
	addItem: (text: string, category: "good" | "bad") => Promise<void>;
	updateItem: (itemId: string, text: string) => Promise<void>;
	mergeItems: (sourceIds: string[], targetText?: string) => Promise<void>;
	voteItem: (itemId: string) => Promise<void>;
	setPhase: (phase: Session["phase"]) => Promise<void>;
	closeSession: () => Promise<void>;
	setTimer: (duration: number) => Promise<void>;
	timerAction: (action: "start" | "stop" | "reset") => Promise<void>;
	deleteTimer: () => Promise<void>;
}

const RetroContext = createContext<RetroContextType | undefined>(undefined);

export function RetroProvider({ children }: { children: ReactNode }) {
	const [state, dispatch] = useReducer(retroReducer, initialState);

	// Initialize userId from sessionStorage
	useEffect(() => {
		let userId = sessionStorage.getItem("retro_userId");
		if (!userId) {
			userId = crypto.randomUUID();
			sessionStorage.setItem("retro_userId", userId);
		}
		dispatch({ type: "SET_USER_ID", payload: userId });
	}, []);

	const createSession = async (userName: string) => {
		try {
			dispatch({ type: "SET_LOADING", payload: true });
			dispatch({ type: "SET_USER_NAME", payload: userName });

			const response = await fetch("/api/session", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ creatorId: state.userId }),
			});
			const data = await response.json();

			if (data.session) {
				// Ensure votes are arrays in the session data
				const session = data.session;
				if (session?.items) {
					Object.values(session.items).forEach((item: any) => {
						if (item.votes && !Array.isArray(item.votes)) {
							item.votes = Array.from(item.votes);
						}
					});
				}
				dispatch({ type: "SET_SESSION", payload: session });
				connectWebSocket(session.id, userName);
			} else {
				dispatch({
					type: "SET_ERROR",
					payload: data.error || "Failed to create session",
				});
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to create session" });
		} finally {
			dispatch({ type: "SET_LOADING", payload: false });
		}
	};

	const joinSession = async (sessionId: string, userName: string) => {
		try {
			dispatch({ type: "SET_LOADING", payload: true });
			dispatch({ type: "SET_USER_NAME", payload: userName });

			// Join the session
			const joinResponse = await fetch(`/api/session/${sessionId}/join`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userId: state.userId, userName }),
			});

			if (!joinResponse.ok) {
				const errorData = await joinResponse.json().catch(() => ({}));
				throw new Error(errorData.error || "Failed to join session");
			}

			// Get session data
			const sessionResponse = await fetch(`/api/session/${sessionId}`);
			if (!sessionResponse.ok) {
				throw new Error("Session not found");
			}
			const data = await sessionResponse.json();

			if (data.session) {
				// Ensure votes are arrays in the session data
				const session = data.session;
				if (session?.items) {
					Object.values(session.items).forEach((item: any) => {
						if (item.votes && !Array.isArray(item.votes)) {
							item.votes = Array.from(item.votes);
						}
					});
				}
				dispatch({ type: "SET_SESSION", payload: session });
				connectWebSocket(sessionId, userName);
			} else {
				throw new Error(data.error || "Session not found");
			}
		} catch (error) {
			dispatch({ type: "SET_LOADING", payload: false });
			throw error; // Re-throw the error so the calling function can handle it
		}
	};

	const connectWebSocket = (sessionId: string, userName: string) => {
		const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
		const wsUrl = `${protocol}//${window.location.host}/ws/${sessionId}`;
		const ws = new WebSocket(wsUrl);

		ws.onopen = () => {
			console.log("WebSocket connected");
			dispatch({ type: "SET_WS", payload: ws });

			// Send join message
			ws.send(
				JSON.stringify({
					type: "join",
					userId: state.userId,
					userName: userName,
				}),
			);
		};

		ws.onmessage = (event) => {
			try {
				const message: WebSocketMessage = JSON.parse(event.data);
				handleWebSocketMessage(message);
			} catch (error) {
				console.error("Error parsing WebSocket message:", error);
			}
		};

		ws.onclose = () => {
			console.log("WebSocket disconnected");
			dispatch({ type: "SET_WS", payload: null });
		};

		ws.onerror = (error) => {
			console.error("WebSocket error:", error);
			dispatch({ type: "SET_ERROR", payload: "Connection error" });
		};
	};

	const handleWebSocketMessage = (message: WebSocketMessage) => {
		switch (message.type) {
			case "sessionUpdated": {
				// Ensure votes are arrays in the session data
				const session = (message.data as any).session;
				if (session?.items) {
					Object.values(session.items).forEach((item: any) => {
						if (item.votes && !Array.isArray(item.votes)) {
							item.votes = Array.from(item.votes);
						}
					});
				}
				dispatch({ type: "SET_SESSION", payload: session });
				break;
			}
			case "sessionClosed":
				dispatch({ type: "CLOSE_SESSION" });
				break;
			case "error":
				dispatch({ type: "SET_ERROR", payload: (message.data as any).message });
				break;
		}
	};

	const addItem = async (text: string, category: "good" | "bad") => {
		if (!state.session) return;

		try {
			const response = await fetch(`/api/session/${state.session.id}/items`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ text, category, userId: state.userId }),
			});

			const data = await response.json();
			if (data.item) {
				dispatch({ type: "ADD_ITEM", payload: data.item });
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to add item" });
		}
	};

	const updateItem = async (itemId: string, text: string) => {
		if (!state.session) return;

		try {
			const response = await fetch(
				`/api/session/${state.session.id}/items/${itemId}`,
				{
					method: "PUT",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ text, userId: state.userId }),
				},
			);

			const data = await response.json();
			if (data.item) {
				dispatch({ type: "UPDATE_ITEM", payload: data.item });
			} else {
				dispatch({
					type: "SET_ERROR",
					payload: data.error || "Failed to update item",
				});
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to update item" });
		}
	};

	const mergeItems = async (sourceIds: string[], targetText?: string) => {
		if (!state.session) return;

		try {
			const response = await fetch(
				`/api/session/${state.session.id}/items/merge`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ sourceIds, targetText, userId: state.userId }),
				},
			);

			const data = await response.json();
			if (data.item) {
				dispatch({
					type: "MERGE_ITEMS",
					payload: { sourceIds, newItem: data.item },
				});
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to merge items" });
		}
	};

	const voteItem = async (itemId: string) => {
		if (!state.session) return;

		try {
			const response = await fetch(`/api/session/${state.session.id}/vote`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ itemId, userId: state.userId }),
			});

			if (response.ok) {
				dispatch({
					type: "VOTE_ITEM",
					payload: { itemId, userId: state.userId },
				});
			} else {
				const errorData = await response.json();
				dispatch({
					type: "SET_ERROR",
					payload: errorData.error || "Failed to vote",
				});
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to vote" });
		}
	};

	const setPhase = async (phase: Session["phase"]) => {
		if (!state.session) return;

		try {
			const response = await fetch(`/api/session/${state.session.id}/phase`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ phase }),
			});

			if (response.ok) {
				dispatch({ type: "SET_PHASE", payload: phase });
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to set phase" });
		}
	};

	const closeSession = async () => {
		if (!state.session) return;

		try {
			const response = await fetch(`/api/session/${state.session.id}/close`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userId: state.userId }),
			});

			if (response.ok) {
				dispatch({ type: "CLOSE_SESSION" });
				if (state.ws) {
					state.ws.close();
				}
			} else {
				const errorData = await response.json();
				dispatch({
					type: "SET_ERROR",
					payload: errorData.error || "Failed to close session",
				});
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to close session" });
		}
	};

	const setTimer = async (duration: number) => {
		if (!state.session) return;

		try {
			const response = await fetch(`/api/session/${state.session.id}/timer`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userId: state.userId, duration }),
			});

			if (!response.ok) {
				throw new Error("Failed to set timer");
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to set timer" });
		}
	};

	const timerAction = async (action: "start" | "stop" | "reset") => {
		if (!state.session) return;

		try {
			const response = await fetch(
				`/api/session/${state.session.id}/timer/action`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ userId: state.userId, action }),
				},
			);

			if (!response.ok) {
				throw new Error("Failed to perform timer action");
			}
		} catch (_error) {
			dispatch({
				type: "SET_ERROR",
				payload: "Failed to perform timer action",
			});
		}
	};

	const deleteTimer = async () => {
		if (!state.session) return;

		try {
			const response = await fetch(`/api/session/${state.session.id}/timer`, {
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userId: state.userId }),
			});

			if (!response.ok) {
				throw new Error("Failed to delete timer");
			}
		} catch (_error) {
			dispatch({ type: "SET_ERROR", payload: "Failed to delete timer" });
		}
	};

	return (
		<RetroContext.Provider
			value={{
				state,
				dispatch,
				createSession,
				joinSession,
				addItem,
				updateItem,
				mergeItems,
				voteItem,
				setPhase,
				closeSession,
				setTimer,
				timerAction,
				deleteTimer,
			}}
		>
			{children}
		</RetroContext.Provider>
	);
}

export function useRetro() {
	const context = useContext(RetroContext);
	if (context === undefined) {
		throw new Error("useRetro must be used within a RetroProvider");
	}
	return context;
}
